import React, { useEffect, useState } from "react";
import { Form, Input, Select, Row, Col, Typography, message, Modal, Checkbox, Alert } from "antd";
import { CommonDropdownParams } from "../../../../constants/CommonDropdownParams";
import { DropdownsResponse } from "../../../../models/ICommon";
import { CommonService } from "../../../../services/Common/CommonService";
import { createCharacterValidatorRule } from "../../../../validators/inputCharacterValidator";
import "./BankDetails.css";
import axios from "axios";
import { InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { BankDetails as IBankDetails } from "../../../../services/Invoices/CreatorProfile/CreatorProfileService";
import LabelWithTooltip from "../../../comman/LabelWithTooltip.tsx/LabelWithTooltip";
import { GlobalButton } from "../../../comman/GlobalButton.styles";

const { Option } = Select;
const { Text } = Typography;

export interface BankFormValues {
	bankName: string;
	accountTypeId: number;
	accountType: string;
	routingNumber: string;
	confirmRoutingNumber: string;
	accountNumber: string;
	confirmAccountNumber: string;
	ibanNumber: string;
	confirmIbanNumber?: string;
	additionalDataJson: string;
}

interface BankDetailsFormProps {
	initialValues?: Partial<IBankDetails>;
	onSave: (values: BankFormValues) => void;
	loading?: boolean;
	countryCode?: string;
}

const BankDetails: React.FC<BankDetailsFormProps> = ({ initialValues, onSave, loading, countryCode }) => {
	const [form] = Form.useForm<BankFormValues>();
	// const [customFields, setCustomFields] = useState<CustomField[]>([]);
	const [dropdowns, setDropdowns] = useState<DropdownsResponse>({});
	const [showAccountNumber, setShowAccountNumber] = useState(false);
	const [showConfirmAccountNumber, setShowConfirmAccountNumber] = useState(false);
	const [showIbanNumber, setShowIbanNumber] = useState(false);
	const [showConfirmIbanNumber, setShowConfirmIbanNumber] = useState(false);
	const [ifscDetails, setIfscDetails] = useState<any>(null);
	const [isIndia, setIsIndia] = useState(false);
	const [confirmVisible, setConfirmVisible] = useState(false);
	const [confirmChecked, setConfirmChecked] = useState(false);
	const [pendingValues, setPendingValues] = useState<BankFormValues | null>(null);

	const accountTypeLabel = dropdowns?.BankAccountType?.find((item) => item.id === pendingValues?.accountTypeId)?.label;
	const routingNumberWatch = Form.useWatch("routingNumber", form);
	const [ifscError, setIfscError] = useState<string>("");

	useEffect(() => {
		getCommonDropdown([CommonDropdownParams.BankAccountType]);
	}, []);

	const getCommonDropdown = (ids: number[]) => {
		CommonService.getAllDropdowns(ids)
			.then((rawData) => {
				setDropdowns(rawData);
			})
			.catch(() => {
				message.error("Failed to load dropdowns");
			});
	};

	// Fetch IFSC details whenever routingNumber changes (India only)
	useEffect(() => {
		if (!isIndia || !routingNumberWatch || routingNumberWatch.length !== 11) {
			setIfscDetails(null);
			return;
		}

		// Optional: Add a small debounce to avoid too many API calls while typing
		const timeoutId = setTimeout(() => {
			fetchIFSCDetails(routingNumberWatch.toUpperCase().trim());
		}, 500); // 500ms debounce

		return () => clearTimeout(timeoutId);
	}, [routingNumberWatch, isIndia]);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue({
				...initialValues,
				confirmRoutingNumber: initialValues.routingNumber,
				confirmAccountNumber: initialValues.accountNumber,
				confirmIbanNumber: initialValues.ibanNumber,		
				accountType: initialValues.ibanNumber ? "iban" : "account"	
			});
		}
		if (countryCode && countryCode === "IND") {
			setIsIndia(true);
		}
	}, [initialValues, form]);

	// ==================== IFSC Validation for India ==0==================
	const fetchIFSCDetails = async (ifsc: string) => {
		if (!ifsc || ifsc.length !== 11) {
			setIfscDetails(null);
			setIfscError("");
			return;
		}

		try {
			const res = await axios.get(`https://ifsc.razorpay.com/${ifsc}`);

			if (res.data && res.data.BANK) {
				debugger;
				setIfscDetails(res.data);

				// Auto-fill Bank Name
				form.setFieldsValue({
					bankName: res.data.BANK
				});
				setIfscError("");
				message.success(`Bank details loaded: ${res.data.bank}`);
			} else {
				setIfscDetails(null);
				setIfscError("Invalid IFSC code. Please check and try again.");
			}
		} catch (error: any) {
			setIfscDetails(null);
			if (ifsc.length === 11) {
				setIfscError("Invalid IFSC code. Please enter a valid IFSC.");
			} else {
				setIfscError("");
			}
		}
	};

	const onFinish = (values: BankFormValues) => {
		// Basic mismatch validation
		if (values.routingNumber !== values.confirmRoutingNumber) {
			message.error("Routing numbers don't match");
			return;
		}
		if (values.accountNumber !== values.confirmAccountNumber) {
			message.error("Account numbers don't match");
			return;
		}

		setPendingValues(values);
		setConfirmVisible(true);
	};

	const handleConfirm = () => {
		if (!confirmChecked) {
			message.error("Please confirm the details first");
			return;
		}

		if (pendingValues) {
			onSave(pendingValues);
		}

		setConfirmVisible(false);
		setConfirmChecked(false);
	};

	// Mask account number (show last 4 digits)
	const maskedAccountNumber = (value: string) => {
		if (!value || value.length <= 4) return value;
		return "•".repeat(value.length - 4) + value.slice(-4);
	};

	return (
		<>
			{/* <Text type="secondary" className="block mb-6">
				Add your bank account for receiving payments. Bank name must match invoice details.
			</Text> */}
			{/* ===== Yellow Warning Banner ===== */}
			<Alert
				message={
					<>
						<WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
						<strong>Add your bank account for receiving payments</strong>
					</>
				}
				description="Incorrect account or routing numbers will cause failed payments, and corrections can delay your payouts by up to a week."
				type="warning"
				showIcon={false}
				style={{
					backgroundColor: "#fffbe6",
					borderLeft: "3px solid #faad14",
					marginBottom: 24
				}}
			/>
			<Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ accountType: "account", ...initialValues }}>
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<Form.Item
							name="bankName"
							label={
								<LabelWithTooltip
									label="Provider Name"
									tooltip="Bank name or E-wallet name like GCash, etc."
								/>
							}
							rules={[{ required: true, message: "Please enter your bank name" }, createCharacterValidatorRule()]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item
							name="accountTypeId"
							label={
								<LabelWithTooltip
									label="Account Type"
									tooltip="Select the type of account you are using to receive payments, such as Checking or Savings."
								/>
							}
							rules={[{ required: true, message: "Please select your account type" }]}
						>
							<Select
								placeholder="Select account type"
								showSearch
								optionFilterProp="label"
								options={dropdowns?.BankAccountType?.map((item) => ({
									value: item.id,
									label: item.label
								}))}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item
							name="accountType"
							label="Account Number Type"
							rules={[{ required: true, message: "Please select your account number type" }]}
						>
							<Select>
								<Option value="account">Account Number</Option>
								<Option value="iban">IBAN Number</Option>
							</Select>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					{/* Account Number + Confirm */}
					<Form.Item noStyle shouldUpdate={(prev, curr) => prev.accountType !== curr.accountType}>
						{({ getFieldValue }) => {
							const type = getFieldValue("accountType") || "account";

							return type === "iban" ? (
								// IBAN Section (same pattern as requested)
								<>
									<Col xs={24} md={12}>
										<Form.Item name="ibanNumber" label="IBAN" rules={[{ required: true, message: "Please enter your IBAN" }]}>
											<Input
												type={showIbanNumber ? "text" : "password"}
												placeholder="Enter account number"
												suffix={
													<Text
														style={{ cursor: "pointer", color: "#1890ff" }}
														onClick={() => setShowIbanNumber(!showIbanNumber)}
													>
														{showIbanNumber ? "Hide" : "Show"}
													</Text>
												}
											/>
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item
											name="confirmIbanNumber"
											label="Confirm IBAN"
											dependencies={["ibanNumber"]}
											rules={[
												{ required: true, message: "Please re-enter IBAN" },
												({ getFieldValue }) => ({
													validator(_, value) {
														if (!value || getFieldValue("ibanNumber") === value) return Promise.resolve();
														return Promise.reject(new Error("IBAN numbers don't match"));
													}
												})
											]}
										>
											<Input
												type={showConfirmIbanNumber ? "text" : "password"}
												placeholder="Re-enter to confirm"
												onPaste={(e) => e.preventDefault()}
												suffix={
													<Text
														style={{ cursor: "pointer", color: "#1890ff" }}
														onClick={() => setShowConfirmIbanNumber(!showConfirmIbanNumber)}
													>
														{showConfirmIbanNumber ? "Hide" : "Show"}
													</Text>
												}
											/>
										</Form.Item>
									</Col>
								</>
							) : (
								<>
									{/* Account Number with Masking */}
									<Col xs={24} md={12}>
										<Form.Item
											name="accountNumber"
											label={<LabelWithTooltip label="Account Number / Wallet ID" tooltip="Bank account number or your e-wallet ID." />}
											rules={[{ required: true, message: "Please enter your account number" }]}
										>
											<Input
												type={showAccountNumber ? "text" : "password"}
												placeholder="Enter account number"
												suffix={
													<Text
														style={{ cursor: "pointer", color: "#1890ff" }}
														onClick={() => setShowAccountNumber(!showAccountNumber)}
													>
														{showAccountNumber ? "Hide" : "Show"}
													</Text>
												}
											/>
										</Form.Item>
									</Col>

									{/* Confirm Account Number */}
									<Col xs={24} md={12}>
										<Form.Item
											name="confirmAccountNumber"
											label="Confirm Account Number"
											dependencies={["accountNumber"]}
											rules={[
												{ required: true, message: "Please re-enter account number" },
												({ getFieldValue }) => ({
													validator(_, value) {
														if (!value || getFieldValue("accountNumber") === value) {
															return Promise.resolve();
														}
														return Promise.reject(new Error("Account numbers don't match"));
													}
												})
											]}
										>
											<Input
												type={showConfirmAccountNumber ? "text" : "password"}
												placeholder="Re-enter to confirm"
												onPaste={(e) => e.preventDefault()}
												suffix={
													<Text
														style={{ cursor: "pointer", color: "#1890ff" }}
														onClick={() => setShowConfirmAccountNumber(!showConfirmAccountNumber)}
													>
														{showConfirmAccountNumber ? "Hide" : "Show"}
													</Text>
												}
											/>
										</Form.Item>
									</Col>

									{/* Routing Number / IFSC */}
									<Col xs={24} md={12}>
										<Form.Item
											name="routingNumber"
											label={
												<LabelWithTooltip
													label="Routing Number"
													tooltip="Routing number, SWIFT, or IFSC · e-wallet name for e-wallets"
												/>
											}
											rules={[
												{ required: true, message: "Please enter your routing number / IFSC" },
												createCharacterValidatorRule()
											]}
											help={ifscError || undefined}
											validateStatus={ifscError ? "error" : undefined}
										>
											<Input
												placeholder={isIndia ? "HDFC0000248" : "123456789"}
												maxLength={isIndia ? 11 : 20}
												style={{ textTransform: isIndia ? "uppercase" : "none" }}
											/>
										</Form.Item>
									</Col>

									{/* Confirm Routing Number */}
									<Col xs={24} md={12}>
										<Form.Item
											name="confirmRoutingNumber"
											label="Confirm Routing Number"
											dependencies={["routingNumber"]}
											rules={[
												{ required: true, message: "Please re-enter routing number" },
												({ getFieldValue }) => ({
													validator(_, value) {
														if (!value || getFieldValue("routingNumber") === value) {
															return Promise.resolve();
														}
														return Promise.reject(new Error("Routing numbers don't match"));
													}
												})
											]}
										>
											<Input placeholder="Re-enter to confirm" onPaste={(e) => e.preventDefault()} />
										</Form.Item>
									</Col>

									{/* IFSC Details Box (India only) */}
									{isIndia && ifscDetails && !ifscError && (
										<Col xs={24}>
											<div
												style={{
													backgroundColor: "#f0f9ff",
													border: "1px solid #bae6fd",
													borderLeft: "4px solid #3b82f6",
													padding: "12px 16px",
													borderRadius: "4px",
													marginBottom: 16
												}}
											>
												<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
													<span style={{ color: "#3b82f6" }}>✅</span>
													<strong>Bank details found — please confirm</strong>
													<span style={{ marginLeft: "auto", fontSize: "12px", color: "#64748b" }}>
														VERIFIED VIA RAZORPAY
													</span>
												</div>

												<div style={{ background: "white", padding: "10px", borderRadius: "4px" }}>
													<div>
														<strong>Bank:</strong> {ifscDetails.BANK}
													</div>
													<div>
														<strong>Branch:</strong> {ifscDetails.BRANCH || "N/A"}
													</div>
												</div>
											</div>
										</Col>
									)}
								</>
							);
						}}
					</Form.Item>
				</Row>

				{/* ===== Blue Security Banner ===== */}
				<Alert
					message={
						<>
							<InfoCircleOutlined style={{ color: "#1890ff", marginRight: 8 }} />
							Your information is encrypted
						</>
					}
					description="Account numbers are stored using bank-grade encryption and are never shared with customers."
					type="info"
					showIcon={false}
					style={{
						backgroundColor: "#e6f7ff",
						borderLeft: "3px solid #1890ff",
						marginTop: 24,
						marginBottom: 24
					}}
				/>

				<div className="d-flex justify-content-center mt-5">
					<GlobalButton className="primary" htmlType="submit" loading={loading} size="large">
						{initialValues ? "Save" : "Save Bank Details"}
					</GlobalButton>
				</div>
			</Form>

			{/* Confirmation Modal */}
			<Modal
				open={confirmVisible}
				title="Verify Your Bank Details"
				onCancel={() => setConfirmVisible(false)}
				footer={[
					<div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
						<GlobalButton key="edit" className="secondary" onClick={() => setConfirmVisible(false)}>
							Edit Details
						</GlobalButton>
						<GlobalButton key="confirm" className="primary" onClick={handleConfirm}>
							Confirm & Continue
						</GlobalButton>
					</div>
				]}
			>
				<p className="mb-3">You will be paid to this account. Please verify the details before continuing.</p>

				<div className="mb-4">
					<div className="d-flex justify-content-between mb-2">
						<span>
							<b>Bank Name</b>
						</span>
						<span>{pendingValues?.bankName}</span>
					</div>

					<div className="d-flex justify-content-between mb-2">
						<span>
							<b>Account Type</b>
						</span>
						<span>{accountTypeLabel}</span>
					</div>

					{pendingValues?.accountType === "iban" ? (
						<>
							<div className="d-flex justify-content-between mb-2">
								<span>
									<b>IBAN</b>
								</span>
								<span>{pendingValues?.ibanNumber}</span>
							</div>
						</>
					) : (
						<>
							<div className="d-flex justify-content-between mb-2">
								<span>
									<b>Routing Number / IFSC</b>
								</span>
								<span>{pendingValues?.routingNumber}</span>
							</div>
							<div className="d-flex justify-content-between">
								<span>
									<b>Account Number</b>
								</span>
								<span>{maskedAccountNumber(pendingValues?.accountNumber || "")}</span>
							</div>
						</>
					)}
				</div>

				<Checkbox checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)}>
					I confirm these details are correct.
				</Checkbox>
			</Modal>
		</>
	);
};

export default BankDetails;
